import { IPublicClientApplication } from '@azure/msal-browser'
import { protectedResources } from '../authConfig'
import { AdImpressionsDto } from '../models/AdImpressionsDto'
import { OrgMetricsDto } from '../models/OrgMetricsDto'
import { AllLocationsBundleSummary } from '../models/Dashboard/AllLocationsBundleSummary'
import { DeviceAdvertisementApprovalDto } from '../models/DeviceAdvertisementApprovalDto'
import { DeviceAdvertisements } from '../models/DeviceAdvertisements'
import { DeviceAdvertisementsForHost } from '../models/DeviceAdvertisementsForHost'
import { Location } from '../models/Location'
import { RunningAdvertisementDto } from '../models/RunningAdvertisementDto'
import {
  authorizedGet,
  authorizedPatch,
  authorizedPost,
} from '../utils/authUtils'

export const fetchMyLocationAds = async (
  organizationId: number,
  instance: IPublicClientApplication,
): Promise<Location[]> => {
  const baseUrl = process.env.REACT_APP_BASE_API_URL
  return await authorizedGet(
    instance,
    `${baseUrl}/locations/advertisements/${organizationId}`,
    protectedResources.scopes.read,
  )
}

export const updateDeviceAdvertisementApprovalStatus = async (
  deviceAdvertisement: DeviceAdvertisementApprovalDto,
  instance: IPublicClientApplication,
): Promise<DeviceAdvertisementsForHost> => {
  const baseUrl = process.env.REACT_APP_BASE_API_URL
  return await authorizedPatch(
    instance,
    `${baseUrl}/DeviceAdvertisements/${deviceAdvertisement.id}/approval`,
    deviceAdvertisement,
    protectedResources.scopes.read,
  )
}

export const fetchMyAdvertisements = async (
  organizationId: number,
  instance: IPublicClientApplication,
): Promise<DeviceAdvertisementsForHost[]> => {
  const baseUrl = process.env.REACT_APP_BASE_API_URL
  const params = new URLSearchParams()
  params.append('organizationId', organizationId.toString())

  return await authorizedGet(
    instance,
    `${baseUrl}/DeviceAdvertisements/myAds?${params.toString()}`,
    protectedResources.scopes.read,
  )
}

export const fetchHostAds = async (
  organizationId: number,
  locationId?: number | null,
  instance?: IPublicClientApplication,
): Promise<DeviceAdvertisementsForHost[]> => {
  if (!instance) {
    throw new Error('MSAL instance is required')
  }

  const baseUrl = process.env.REACT_APP_BASE_API_URL
  const params = new URLSearchParams()
  params.append('organizationId', organizationId.toString())
  if (locationId) {
    params.append('locationId', locationId.toString())
  }

  return await authorizedGet(
    instance,
    `${baseUrl}/deviceAdvertisements/hostAds?${params.toString()}`,
    protectedResources.scopes.read,
  )
}

export const cancelDeviceAdvertisementAutoRenew = async (
  adId: number,
  instance: IPublicClientApplication,
): Promise<DeviceAdvertisementsForHost> => {
  const baseUrl = process.env.REACT_APP_BASE_API_URL
  return await authorizedPatch(
    instance,
    `${baseUrl}/deviceAdvertisements/${adId}/cancelAutoRenew`,
    { autoRenew: false },
    protectedResources.scopes.read,
  )
}

export const updateDeviceAdvertisementStatus = async (
  adId: number,
  instance: IPublicClientApplication,
  isEnabled: boolean,
): Promise<DeviceAdvertisementsForHost> => {
  const baseUrl = process.env.REACT_APP_BASE_API_URL
  const action = isEnabled ? 'enable' : 'disable'
  return await authorizedPatch(
    instance,
    `${baseUrl}/deviceAdvertisements/${adId}/${action}`,
    {},
    protectedResources.scopes.read,
  )
}

export const hostUploadAd = async (
  data: {
    deviceId: number
    organizationId: number
    name?: string
    imageUrl: string
    startDateLocal?: string | null
    endDateLocal?: string | null
    timezone?: string
  },
  instance: IPublicClientApplication,
): Promise<DeviceAdvertisements> => {
  const baseUrl = process.env.REACT_APP_BASE_API_URL
  return await authorizedPost(
    instance,
    `${baseUrl}/deviceAdvertisements/hostUpload`,
    data,
    protectedResources.scopes.read,
  )
}

export const fetchRunningAdsByDeviceId = async (
  deviceId: number,
  instance: IPublicClientApplication,
): Promise<RunningAdvertisementDto[]> => {
  const baseUrl = process.env.REACT_APP_BASE_API_URL
  return await authorizedGet(
    instance,
    `${baseUrl}/advertisements/devices/${deviceId}?source=website`,
    protectedResources.scopes.read,
  )
}

export const fetchAllAdsByDeviceId = async (
  deviceId: number,
  instance: IPublicClientApplication,
): Promise<DeviceAdvertisementsForHost[]> => {
  const baseUrl = process.env.REACT_APP_BASE_API_URL

  return await authorizedGet(
    instance,
    `${baseUrl}/deviceAdvertisements/devices/${deviceId}`,
    protectedResources.scopes.read,
  )
}

export const fetchDeviceAdvertisementDetails = async (
  advertisementId: number,
  instance: IPublicClientApplication,
): Promise<DeviceAdvertisementsForHost> => {
  return authorizedGet<DeviceAdvertisementsForHost>(
    instance,
    `${process.env.REACT_APP_BASE_API_URL}/deviceAdvertisements/${advertisementId}`,
    protectedResources.scopes.read,
  )
}

export const fetchPendingApprovalCount = async (
  organizationId: number,
  instance: IPublicClientApplication,
): Promise<number> => {
  const baseUrl = process.env.REACT_APP_BASE_API_URL
  const params = new URLSearchParams()
  params.append('organizationId', organizationId.toString())
  params.append('status', 'PendingApproval')

  const ads = await authorizedGet<DeviceAdvertisementsForHost[]>(
    instance,
    `${baseUrl}/deviceAdvertisements/hostAds?${params.toString()}`,
    protectedResources.scopes.read,
  )

  return ads.length
}

export const fetchAdImpressions = async (
  advertisementId: number,
  instance: IPublicClientApplication,
  startDate?: string,
  endDate?: string,
): Promise<AdImpressionsDto> => {
  const baseUrl = process.env.REACT_APP_BASE_API_URL
  const params = new URLSearchParams()
  if (startDate) params.append('startDate', startDate)
  if (endDate) params.append('endDate', endDate)
  const query = params.toString() ? `?${params.toString()}` : ''

  return await authorizedGet(
    instance,
    `${baseUrl}/advertisements/${advertisementId}/impressions${query}`,
    protectedResources.scopes.read,
  )
}

export const fetchOrgMetrics = async (
  organizationId: number,
  instance: IPublicClientApplication,
  days = 90,
): Promise<OrgMetricsDto> => {
  return authorizedGet<OrgMetricsDto>(
    instance,
    `${process.env.REACT_APP_BASE_API_URL}/advertisements/metrics?organizationId=${organizationId}&days=${days}`,
    protectedResources.scopes.read,
  )
}

export const fetchAllLocationsBundleSummary = async (
  advertisementId: number,
  instance: IPublicClientApplication,
): Promise<AllLocationsBundleSummary> => {
  return authorizedGet<AllLocationsBundleSummary>(
    instance,
    `${process.env.REACT_APP_BASE_API_URL}/DeviceAdvertisements/all-locations-summary/${advertisementId}`,
    protectedResources.scopes.read,
  )
}

export interface AllLocationsAdvertisementRef {
  advertisementId: number
  name: string
  imageUrl?: string | null
  startDateLocal?: string | null
  endDateLocal?: string | null
  autoRenew: boolean
  numberOfDays?: number | null
  isPast: boolean
}

export const fetchAllLocationsAdvertisements = async (
  organizationId: number,
  instance: IPublicClientApplication,
): Promise<AllLocationsAdvertisementRef[]> =>
  authorizedGet<AllLocationsAdvertisementRef[]>(
    instance,
    `${process.env.REACT_APP_BASE_API_URL}/advertisements/all-locations?organizationId=${organizationId}`,
    protectedResources.scopes.read,
  )
